import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, User, Users, MessageCircle, Heart, Zap, Repeat2, AtSign, Lock, Mail, PenLine, Star, Check, Settings, X, SlidersHorizontal, BellOff, BellRing, UserPlus, Smartphone, Loader2 } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import {
  isPushSupported,
  isPushEnabled,
  enablePushNotifications,
  disablePushNotifications,
} from '../lib/pushNotifications';
import { useNotifications, useUnreadDMs, useNotifPrefs, type Notification, type NotifType } from '../hooks/useNotifications';
import { useFollows } from '../hooks/useFollows';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useDisplayName } from '../hooks/useNip05Verified';
import { useNostrPublish } from '../hooks/useNostrPublish';
import BCHTipButton from '../components/BCHTipButton';
import VerifiedBadge from '../components/VerifiedBadge';
import { resolveTipTokens, describeTip } from '../lib/tipTokens';

type FilterTab = 'all' | 'reply' | 'reaction' | 'zap' | 'mention' | 'repost' | 'dm' | 'friend_post' | 'favorite_post' | 'referral';

// Separate "page view" – notifications vs manage
type PageView = 'feed' | 'manage';

const FILTER_TABS: { id: FilterTab; label: string; icon: React.ReactNode; activeClass: string; badgeClass: string; inactiveBadge: string }[] = [
  { id: 'all',           label: 'All',       icon: <Bell className="w-3.5 h-3.5" />,           activeClass: 'bg-primary text-primary-foreground',                                    badgeClass: 'bg-primary-foreground/20 text-primary-foreground', inactiveBadge: 'bg-primary/10 text-primary' },
  { id: 'favorite_post', label: 'Favorites', icon: <Star className="w-3.5 h-3.5" />,           activeClass: 'bg-amber-500 text-white',                                               badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  { id: 'friend_post',   label: 'Following', icon: <Users className="w-3.5 h-3.5" />,          activeClass: 'bg-sky-500 text-white',                                                 badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  { id: 'reply',       label: 'Replies',  icon: <MessageCircle className="w-3.5 h-3.5" />,  activeClass: 'bg-amber-500 text-white',                                               badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  { id: 'reaction',    label: 'Likes',    icon: <Heart className="w-3.5 h-3.5" />,          activeClass: 'bg-red-500 text-white',                                                  badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-red-500/10 text-red-500' },
  { id: 'zap',         label: 'Zaps',     icon: <Zap className="w-3.5 h-3.5" />,            activeClass: 'bg-emerald-500 text-white',                                              badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  { id: 'dm',          label: 'Messages', icon: <Mail className="w-3.5 h-3.5" />,           activeClass: 'bg-cyan-500 text-white',                                                badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' },
  { id: 'mention',     label: 'Mentions', icon: <AtSign className="w-3.5 h-3.5" />,         activeClass: 'bg-blue-500 text-white',                                                 badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-blue-500/10 text-blue-500' },
  { id: 'repost',      label: 'Reposts',  icon: <Repeat2 className="w-3.5 h-3.5" />,        activeClass: 'bg-purple-500 text-white',                                               badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-purple-500/10 text-purple-500' },
  { id: 'referral',    label: 'Referrals', icon: <UserPlus className="w-3.5 h-3.5" />,      activeClass: 'bg-green-500 text-white',                                                badgeClass: 'bg-white/20 text-white',                          inactiveBadge: 'bg-green-500/10 text-green-600 dark:text-green-400' },
];

export default function NotificationsPage() {
  const { user, login } = useAppContext();
  const { follows } = useFollows(user?.pubkey);
  const { notifications, unreadCount, markAllRead, lastReadTime } = useNotifications(user?.pubkey, follows);
  const { unreadDMs } = useUnreadDMs(user?.pubkey);
  const { prefs, togglePref } = useNotifPrefs();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterTab>('all');
  const [pageView, setPageView] = useState<PageView>('feed');

  // Capture the read time at mount so unread highlights persist while viewing
  const [viewReadTime] = useState(() => lastReadTime);

  // Resolve the tip-token allowlist so token tips show the right name/decimals
  // (e.g. "13 FURU") instead of a bare base-unit amount.
  useEffect(() => { resolveTipTokens().catch(() => {}); }, []);

  // Auto-mark all as read after a short delay so user sees the unread highlights first
  useEffect(() => {
    if (unreadCount === 0) return;
    const timer = setTimeout(() => markAllRead(), 2000);
    return () => clearTimeout(timer);
  }, [unreadCount, markAllRead]);

  // Count per type (for badge numbers on tabs)
  const typeCounts = useMemo(() => {
    const counts: Record<string, { total: number; unread: number }> = {};
    for (const tab of FILTER_TABS) {
      counts[tab.id] = { total: 0, unread: 0 };
    }
    for (const n of notifications) {
      counts['all'].total++;
      if (n.timestamp > viewReadTime) counts['all'].unread++;
      if (counts[n.type]) {
        counts[n.type].total++;
        if (n.timestamp > viewReadTime) counts[n.type].unread++;
      }
    }
    // DM tab always shows as available with unread count from the DM hook
    counts['dm'] = { total: Math.max(1, unreadDMs), unread: unreadDMs };
    return counts;
  }, [notifications, viewReadTime, unreadDMs]);

  // Filter and split into unread/read.
  // When on "All" tab, respect user's notification preferences (toggled types).
  // When on a specific type tab, show all of that type regardless of prefs.
  const { unread, read } = useMemo(() => {
    let filtered: Notification[];
    if (filter === 'all') {
      // Apply user preferences — only show types they haven't toggled off
      filtered = notifications.filter(n => prefs[n.type] !== false);
    } else {
      filtered = notifications.filter(n => n.type === filter);
    }

    const unread: Notification[] = [];
    const read: Notification[] = [];
    for (const n of filtered) {
      if (n.timestamp > viewReadTime) {
        unread.push(n);
      } else {
        read.push(n);
      }
    }
    return { unread, read };
  }, [notifications, viewReadTime, filter, prefs]);

  const filteredTotal = unread.length + read.length;

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <Bell className="w-12 h-12 text-muted-foreground/20 mb-4" />
        <p className="text-sm font-semibold mb-1">Log in to see notifications</p>
        <p className="text-xs text-muted-foreground mb-4">Replies, reactions, reposts and mentions will show up here.</p>
        <button onClick={login} className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:opacity-90">
          Log In
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="font-bold text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" /> Notifications
            {typeCounts['all'].unread > 0 && (
              <span className="text-xs font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                {typeCounts['all'].unread} new
              </span>
            )}
          </h1>
          <div className="flex items-center gap-2">
            {notifications.length > 0 && pageView === 'feed' && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>
        </div>

        {/* Two-tab toggle: Notifications / Manage */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setPageView('feed')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              pageView === 'feed'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <BellRing className="w-4 h-4" />
            Notifications
          </button>
          <button
            onClick={() => setPageView('manage')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              pageView === 'manage'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Manage
          </button>
        </div>

        {/* Filter tabs — only shown on the feed view */}
        {pageView === 'feed' && (notifications.length > 0 || unreadDMs > 0) && (
          <div className="flex gap-0.5 px-4 py-2 overflow-x-auto scrollbar-none">
            {FILTER_TABS.map(tab => {
              const counts = typeCounts[tab.id];
              const isActive = filter === tab.id;
              // Hide tabs with zero notifications (except "All" and "dm")
              if (tab.id !== 'all' && tab.id !== 'dm' && counts.total === 0) return null;

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'dm') {
                      navigate('/messages');
                    } else {
                      setFilter(tab.id);
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? tab.activeClass
                      : 'bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  {counts.unread > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums ${
                      isActive ? tab.badgeClass : tab.inactiveBadge
                    }`}>
                      {counts.unread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Manage view ─────────────────────────────────────────── */}
      {pageView === 'manage' && (
        <div className="px-4 py-4 space-y-5 max-w-lg mx-auto">
          <PushToggleCard />
          <NotificationSettings prefs={prefs} onToggle={togglePref} embedded />
        </div>
      )}

      {/* ── Feed view ───────────────────────────────────────────── */}
      {pageView === 'feed' && (
        <>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
              <Bell className="w-12 h-12 text-muted-foreground/20 mb-4" />
              <p className="text-sm font-semibold mb-1">All caught up!</p>
              <p className="text-xs text-muted-foreground">Reactions, replies, and reposts will show up here.</p>
            </div>
          ) : filteredTotal === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
              {FILTER_TABS.find(t => t.id === filter)?.icon && (
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4 text-muted-foreground/30">
                  {React.cloneElement(FILTER_TABS.find(t => t.id === filter)!.icon as React.ReactElement, { className: 'w-6 h-6' })}
                </div>
              )}
              <p className="text-sm font-semibold mb-1">No {FILTER_TABS.find(t => t.id === filter)?.label.toLowerCase()}</p>
              <p className="text-xs text-muted-foreground">
                {filter === 'reply' && 'When someone replies to your posts, they\'ll show up here.'}
                {filter === 'reaction' && 'When someone likes your posts, they\'ll show up here.'}
                {filter === 'zap' && 'When someone zaps you, they\'ll show up here.'}
                {filter === 'dm' && 'Your private messages will show up here.'}
                {filter === 'mention' && 'When someone mentions you, they\'ll show up here.'}
                {filter === 'repost' && 'When someone reposts your posts, they\'ll show up here.'}
                {filter === 'friend_post' && 'When people you follow make new posts, they\'ll show up here.'}
                {filter === 'favorite_post' && 'Star someone on their profile to get notified when they post.'}
                {filter === 'referral' && 'When someone signs up using your referral link, they\'ll show up here.'}
              </p>
            </div>
          ) : (
            <div>
              {/* Unread section */}
              {unread.length > 0 && (
                <div>
                  {unread.map(notif => (
                    <NotificationRow key={notif.id} notif={notif} isUnread />
                  ))}
                  {/* Divider between unread and read */}
                  {read.length > 0 && (
                    <div className="flex items-center gap-3 px-4 py-2 bg-muted/30">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Earlier</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  )}
                </div>
              )}
              {/* Read section */}
              {read.map(notif => (
                <NotificationRow key={notif.id} notif={notif} isUnread={false} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Notification Settings / Manage View ──────────────────────────
// Full-page toggle list so users can easily control what they see.

const TOGGLE_ITEMS: { type: NotifType; label: string; description: string; icon: React.ReactNode; color: string; bgColor: string }[] = [
  { type: 'reply',         label: 'Replies',       description: 'When someone replies to your posts',     icon: <MessageCircle className="w-4 h-4" />, color: 'text-amber-500',   bgColor: 'bg-amber-500/10' },
  { type: 'reaction',      label: 'Likes',         description: 'When someone reacts to your posts',     icon: <Heart className="w-4 h-4" />,          color: 'text-red-500',     bgColor: 'bg-red-500/10' },
  { type: 'zap',           label: 'Zaps / Tips',   description: 'When someone tips you BCH',             icon: <Zap className="w-4 h-4" />,            color: 'text-emerald-500', bgColor: 'bg-emerald-500/10' },
  { type: 'mention',       label: 'Mentions',      description: 'When someone mentions you in a post',   icon: <AtSign className="w-4 h-4" />,         color: 'text-blue-500',    bgColor: 'bg-blue-500/10' },
  { type: 'repost',        label: 'Reposts',       description: 'When someone reposts your content',     icon: <Repeat2 className="w-4 h-4" />,        color: 'text-purple-500',  bgColor: 'bg-purple-500/10' },
  { type: 'friend_post',   label: 'Following',     description: 'New posts from people you follow',      icon: <Users className="w-4 h-4" />,          color: 'text-sky-500',     bgColor: 'bg-sky-500/10' },
  { type: 'favorite_post', label: 'Favorites',     description: 'New posts from your starred users',     icon: <Star className="w-4 h-4" />,           color: 'text-amber-500',   bgColor: 'bg-amber-500/10' },
  { type: 'referral',      label: 'Referrals',     description: 'When someone signs up using your link', icon: <UserPlus className="w-4 h-4" />,       color: 'text-green-500',   bgColor: 'bg-green-500/10' },
];

// ── Push Notifications toggle (Web Push via Netlify backend) ──────
// Lets users opt into OS-level push notifications for zaps, replies,
// and mentions — delivered even when the app is closed. Backed by the
// serverless functions in netlify/functions/push-*.mjs.

function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS uses navigator.standalone; others use the display-mode media query.
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  const mq = window.matchMedia?.('(display-mode: standalone)')?.matches === true;
  return iosStandalone || mq;
}

function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function PushToggleCard() {
  const { user } = useAppContext();
  const [supported] = useState(() => isPushSupported());
  const [enabled, setEnabled] = useState(() => isPushEnabled());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On iOS, Web Push only works when the site is installed to the Home
  // Screen (Apple restriction). Warn the user if they're in a browser tab.
  const iosNotInstalled = isIOSDevice() && !isStandalonePWA();

  const handleToggle = async () => {
    if (!user || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (enabled) {
        const res = await disablePushNotifications(user.pubkey);
        if (res.ok) setEnabled(false);
        else setError(res.error || 'Failed to disable notifications.');
      } else {
        const res = await enablePushNotifications(user.pubkey);
        if (res.ok) setEnabled(true);
        else setError(res.error || 'Failed to enable notifications.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold">Push Notifications</h3>
              <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                Get alerted about zaps, replies, and mentions — even when BCHNostr is closed.
              </p>
            </div>

            {supported && !iosNotInstalled && (
              <button
                onClick={handleToggle}
                disabled={busy}
                aria-label={enabled ? 'Disable push notifications' : 'Enable push notifications'}
                className={`w-11 h-6 rounded-full flex items-center transition-colors shrink-0 disabled:opacity-60 ${
                  enabled ? 'bg-primary justify-end' : 'bg-muted-foreground/20 justify-start'
                }`}
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin mx-1" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-white shadow-sm mx-0.5" />
                )}
              </button>
            )}
          </div>

          {/* Status / guidance messages */}
          {!supported && (
            <p className="text-[11px] text-muted-foreground mt-2 bg-muted/50 rounded-lg px-3 py-2">
              Your browser doesn't support push notifications.
            </p>
          )}
          {supported && iosNotInstalled && (
            <p className="text-[11px] text-muted-foreground mt-2 bg-muted/50 rounded-lg px-3 py-2">
              On iPhone/iPad, add BCHNostr to your Home Screen (Share → “Add to Home Screen”),
              then open it from there to enable notifications.
            </p>
          )}
          {enabled && supported && (
            <p className="text-[11px] text-emerald-500 font-medium mt-2 flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Notifications are on.
            </p>
          )}
          {error && (
            <p className="text-[11px] text-red-500 mt-2 bg-red-500/10 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationSettings({
  prefs,
  onToggle,
  embedded = false,
}: {
  prefs: Record<NotifType, boolean>;
  onToggle: (type: NotifType) => void;
  embedded?: boolean;
}) {
  const enabledCount = TOGGLE_ITEMS.filter(item => prefs[item.type] !== false).length;

  return (
    <div className={embedded ? 'space-y-5' : 'px-4 py-4 space-y-5 max-w-lg mx-auto'}>
      {/* Header card */}
      <div className="rounded-xl border border-border bg-muted/30 p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <SlidersHorizontal className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-bold">Manage Notifications</h3>
            <p className="text-xs text-muted-foreground">
              {enabledCount} of {TOGGLE_ITEMS.length} types enabled
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Toggle which notification types appear in your feed and count toward the bell badge.
          Disabled types won't show in "All" but you can still view them via their tab.
        </p>
      </div>

      {/* Toggle list */}
      <div className="space-y-2">
        {TOGGLE_ITEMS.map(item => {
          const enabled = prefs[item.type] !== false;
          return (
            <button
              key={item.type}
              onClick={() => onToggle(item.type)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${
                enabled
                  ? 'bg-card border border-border shadow-sm'
                  : 'bg-muted/40 border border-transparent opacity-60'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                enabled ? item.bgColor + ' ' + item.color : 'bg-muted text-muted-foreground'
              }`}>
                {item.icon}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-semibold">{item.label}</p>
                <p className="text-[11px] text-muted-foreground leading-snug">{item.description}</p>
              </div>
              {/* Toggle switch */}
              <div className={`w-11 h-6 rounded-full flex items-center transition-colors shrink-0 ${
                enabled ? 'bg-primary justify-end' : 'bg-muted-foreground/20 justify-start'
              }`}>
                <div className="w-5 h-5 rounded-full bg-white shadow-sm mx-0.5" />
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[10px] text-muted-foreground text-center pt-1">
        Changes are saved automatically
      </p>
    </div>
  );
}

// Types of notifications where the user can like/tip the event content
const ACTIONABLE_TYPES = new Set(['reply', 'mention', 'friend_post', 'favorite_post']);

function NotificationRow({ notif, isUnread }: { notif: Notification; isUnread: boolean }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(notif.fromPubkey);
  const name = useDisplayName(notif.fromPubkey, profile, notif.fromPubkey.slice(0, 8) + '...');
  const [liked, setLiked] = useState(false);

  const icons: Record<string, { emoji: string; label: string; color: string }> = {
    reply:       { emoji: '\u{1F4AC}', label: 'replied to you',     color: 'text-blue-500' },
    reaction:    { emoji: '❤\uFE0F', label: 'liked your post',    color: 'text-red-500' },
    repost:      { emoji: '\u{1F504}', label: 'reposted your post', color: 'text-emerald-500' },
    zap:         { emoji: '\u26A1', label: 'zapped you',          color: 'text-amber-500' },
    mention:     { emoji: '\u{1F4E2}', label: 'mentioned you',       color: 'text-purple-500' },
    friend_post:   { emoji: '\u{1F4DD}', label: 'posted',             color: 'text-sky-500' },
    favorite_post: { emoji: '⭐', label: 'posted',             color: 'text-amber-500' },
    referral:      { emoji: '\u{1F389}', label: 'signed up using your referral link!', color: 'text-green-500' },
  };
  const info = icons[notif.type] || icons.mention;

  // Tip receipts (kind 9735) carry an amount + optional token name. Turn the
  // generic "zapped you" into "tipped you 13 FURU" / "tipped you $0.25", and
  // style token tips distinctly (coin emoji + token icon).
  const tipDesc = notif.type === 'zap' && notif.tip ? describeTip(notif.tip) : null;
  const display = tipDesc
    ? {
        emoji: tipDesc.isToken ? '\u{1FA99}' /* 🪙 coin */ : '\u26A1' /* ⚡ */,
        label: `tipped you ${tipDesc.amountLabel}`,
        color: tipDesc.isToken ? 'text-emerald-500' : 'text-amber-500',
        iconUrl: tipDesc.isToken ? tipDesc.iconUrl : undefined,
      }
    : { ...info, iconUrl: undefined as string | undefined };

  const noteLinkTo = notif.type === 'referral'
    ? `/profile/${notif.fromPubkey}`
    : notif.type === 'reply' || notif.type === 'mention' || notif.type === 'friend_post' || notif.type === 'favorite_post'
      ? `/note/${notif.event.id}`
      : notif.relatedEventId
        ? `/note/${notif.relatedEventId}`
        : null;

  const canAct = ACTIONABLE_TYPES.has(notif.type) && user;

  const handleLike = useCallback(async () => {
    if (!user || liked) return;
    setLiked(true);
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [['e', notif.event.id], ['p', notif.fromPubkey]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {
      setLiked(false);
    }
  }, [user, liked, publish, notif.event.id, notif.fromPubkey]);

  const timeAgoStr = (() => {
    const secs = Math.floor(Date.now() / 1000 - notif.timestamp);
    if (secs < 60) return 'just now';
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  })();

  return (
    <div className={`flex items-start gap-3 px-4 py-3.5 transition-colors border-b border-border last:border-0 ${
      isUnread
        ? 'bg-primary/[0.06] border-l-[3px] border-l-primary'
        : 'hover:bg-muted/30 opacity-75'
    }`}>
      {/* Unread dot */}
      <div className="flex flex-col items-center gap-1 pt-1.5 shrink-0 w-3">
        {isUnread && (
          <div className="w-2.5 h-2.5 rounded-full bg-primary shadow-sm shadow-primary/30 animate-pulse" />
        )}
      </div>

      <Link to={`/profile/${notif.fromPubkey}`} className="shrink-0 mt-0.5">
        {profile?.picture ? (
          <img
            src={profile.picture}
            alt={name}
            className={`w-10 h-10 rounded-full object-cover transition-all ${
              isUnread ? 'ring-2 ring-primary/30' : 'hover:ring-2 hover:ring-primary/40'
            }`}
          />
        ) : (
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-base transition-all ${
            isUnread ? 'bg-primary/15 ring-2 ring-primary/30' : 'bg-primary/10 hover:ring-2 hover:ring-primary/40'
          }`}>
            {display.emoji}
          </div>
        )}
      </Link>

      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${isUnread ? '' : 'text-muted-foreground'}`}>
          <Link to={`/profile/${notif.fromPubkey}`} className={`font-semibold hover:underline transition-colors ${
            isUnread ? 'text-foreground hover:text-primary' : 'text-foreground/70 hover:text-primary'
          }`}>
            {name}
          </Link>
          <VerifiedBadge
            nip05={profile?.nip05}
            pubkey={notif.fromPubkey}
            size="xs"
            className="ml-0.5 align-text-bottom"
          />{' '}
          <span className={isUnread ? display.color + ' font-medium' : 'text-muted-foreground'}>
            {display.iconUrl && (
              <img
                src={display.iconUrl}
                alt=""
                className="inline-block w-3.5 h-3.5 rounded-full align-text-bottom mr-0.5"
                loading="lazy"
              />
            )}
            {display.label}
          </span>
        </p>

        {(notif.type === 'reply' || notif.type === 'friend_post' || notif.type === 'favorite_post') && notif.event.content && (
          <p className={`text-xs mt-1 line-clamp-2 ${isUnread ? 'text-foreground/70' : 'text-muted-foreground/60'}`}>
            {notif.event.content.slice(0, 180)}
          </p>
        )}
        {notif.type === 'reaction' && notif.event.content && notif.event.content !== '+' && (
          <p className="text-base mt-0.5">{notif.event.content}</p>
        )}

        <div className="flex items-center gap-2 mt-1.5">
          <span className={`text-[11px] ${isUnread ? 'text-primary/70 font-medium' : 'text-muted-foreground'}`}>{timeAgoStr}</span>

          {/* Inline like & tip actions */}
          {canAct && (
            <>
              <span className="text-border">·</span>
              <button
                onClick={handleLike}
                disabled={liked}
                className={`flex items-center gap-1 text-[11px] font-medium transition-colors ${
                  liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                }`}
              >
                <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-current' : ''}`} />
                {liked ? 'Liked' : 'Like'}
              </button>
              <span className="text-border">·</span>
              <BCHTipButton
                recipientPubkey={notif.fromPubkey}
                recipientProfile={profile}
                eventId={notif.event.id}
              />
            </>
          )}

          {canAct && <span className="text-border">·</span>}
          {noteLinkTo && (
            <Link
              to={noteLinkTo}
              className="text-[11px] text-primary hover:underline font-medium"
            >
              {notif.type === 'referral' ? 'View profile' : 'View post'} &rarr;
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

